import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { QueryResponse } from '../../core/services/QueryResponse';
import { ModelResponse } from '../../core/services/ModelResponse';
import { ExtractModel } from "../models/ExtractModel";
import { ExtractListModel } from "../models/ExtractListModel";
import { OptionModel } from "../models/OptionModel";
import { ExtractPageViewModel } from "../models/ExtractPageViewModel";
import { CalOmsExtractWorkspaceModel } from "../models/CalOmsExtractWorkspaceModel";
import { CalOmsExtractWorkspaceFilterModel } from "../models/CalOmsExtractWorkspaceFilterModel";
import { switchMap } from 'rxjs/operators';
import { DownloadFileService } from '../../common/utilities/DownloadFileService';


@Injectable()
export class ExtractsService {
  constructor(public httpClient: HttpClient,
    public downloadFileService: DownloadFileService
  ) { }

  getExtractCategoryList(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string): Observable<QueryResponse<ExtractModel>> {
    var params = new HttpParams()
      .set('skip', (page * pageSize).toString())
      .set('take', pageSize.toString());
    if (sort) {
      params = params.set('sort', sort)
        .set('sortDirection', sortDirection);
    }
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    return this.httpClient.get<QueryResponse<ExtractModel>>("/api/ExtractsModule/ExtractCategories/ListQuery", { params: params });
  }

  getExtractList(page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string, model: ExtractModel = null): Observable<QueryResponse<ExtractListModel>> {
    var extractPageViewModel = new ExtractPageViewModel();
    extractPageViewModel.page = page;
    extractPageViewModel.pageSize = pageSize;
    extractPageViewModel.skip = page * pageSize;
    extractPageViewModel.take = pageSize;
    extractPageViewModel.sort = sort;
    extractPageViewModel.sortDirection = sortDirection;
    extractPageViewModel.searchTerm = searchTerm;
    if (model != null) {
      extractPageViewModel.model = model;
    }
    return this.httpClient.post<QueryResponse<ExtractListModel>>("/api/ExtractsModule/Extracts/ListQuery", extractPageViewModel);
  }

  getExtractWorkspaceList(key: string, page: number, pageSize: number, sort: string, sortDirection: string, searchTerm: string, extractType: string, model: ExtractModel = null, filterModel: CalOmsExtractWorkspaceFilterModel = null): Observable<QueryResponse<CalOmsExtractWorkspaceModel>> {
    var extractPageViewModel = new ExtractPageViewModel();
    extractPageViewModel.page = page;
    extractPageViewModel.pageSize = pageSize;
    extractPageViewModel.skip = page * pageSize;
    extractPageViewModel.take = pageSize;
    extractPageViewModel.sort = sort;
    extractPageViewModel.sortDirection = sortDirection;
    extractPageViewModel.searchTerm = searchTerm;
    extractPageViewModel.extractType = extractType;
    if (model != null) {
      extractPageViewModel.model = model;
    }
    if (filterModel != null) {
      extractPageViewModel.filterModel = filterModel;
    }
    return this.httpClient.post<QueryResponse<CalOmsExtractWorkspaceModel>>(`/api/ExtractsModule/Extracts/WorkSpaceListQuery/${key}`, extractPageViewModel);
  }

  finalizeExtract(extractKey: string, model: ExtractModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/ExtractsModule/${extractKey}/Finalize/`, { model: model });
  }

  deleteExtract(extractKey: string, model: ExtractModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/ExtractsModule/${extractKey}/Delete/`, { model: model });
  }

  generateExtract(model: ExtractPageViewModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/ExtractsModule/GenerateExtract/`, { model: model });
  }

  generateChildExtracts(extractKey: string, model: ExtractModel): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/ExtractsModule/${extractKey}/GenerateChildExtracts/`, { model: model });
  }

  saveExtract(models: CalOmsExtractWorkspaceModel[]): Observable<ModelResponse<string>> {
    return this.httpClient.post<ModelResponse<string>>(`/api/ExtractsModule/Save/`, { model: models });
  }

  downloadS3ClaimExtract837(key: string): Observable<string> {
    return this.httpClient.get<string>('/api/ExtractsModule/Extracts/ClaimExtract837/' + key);
  }

  downloadFile(key: string, model: OptionModel<any>): Observable<ModelResponse<ExtractListModel>> {
    var url = `${model.url}/${key}`;
    return this.httpClient.get<Blob>(url, { responseType: 'blob' as 'json', observe: 'response' }).map((r) => {
      if (key != null){
        var model = new ExtractListModel ();
        this.downloadFileService.export(r, model);
        var response = new ModelResponse<ExtractListModel>();
        response.model = model;
        return response;
      }
    });
  }

  openExtract(url: string, model: any): void {
    this.downloadFileService.openExtract(url, model);
  }

  downloadFileStart(key: string) {
    return this.downloadS3ClaimExtract837(key).pipe(
      switchMap((url: string) => {
        if (url) {
          return this.httpClient.get<string>(url, { responseType: 'arraybuffer' as 'json', observe: 'response' })
            .map((response: any) => {
              if (!response) {
                return;
              }
              let filename = '';
              let disposition = response.headers.get('Content-Disposition');
              if (!disposition) {
                const urlParams = new URLSearchParams(response.url);
                disposition = urlParams.get('response-content-disposition');
              }

              if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                  filename = matches[1];
                  if (filename.toLowerCase().startsWith("utf-8''")) {
                    filename = decodeURIComponent(filename.replace(/utf-8''/i, ''));
                  } else {
                    filename = filename.replace(/['"]/g, '');
                  }
                }
              }
              
              const type = response.headers.get('Content-Type');

              const blob = new Blob([response.body], { type: type });
              const URL = window.URL || (window as any).webkitURL;
              const downloadUrl = URL.createObjectURL(blob);

              if (filename) {
                // use HTML5 a[download] attribute to specify filename
                const a = document.createElement('a');
                // safari doesn't support this yet
                if (typeof a.download === 'undefined') {
                  (window as any).location = downloadUrl;
                } else {
                  a.href = downloadUrl;
                  a.download = filename;
                  a.className = 'hidden';
                  a.innerText = 'Download Link';
                  document.body.appendChild(a);
                  a.click();
                }
              } else {
                (window as any).location = downloadUrl;
              }

              setTimeout(function () {
                URL.revokeObjectURL(downloadUrl);
              }, 100);
              
            });
        }
      })
    )
  }

}



// WEBPACK FOOTER //
// ./src/app/extracts/services/ExtractsService.ts